import { Dialog } from '@/components/UI/Dialog'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { applyLocaleAndReload } from '@/core/i18n/localeReload'
import { useTranslation } from 'react-i18next'

type Language = {
  id: number
  name: string
  label: string
  code: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  languages: Language[]
}

export function LanguageDialog({ isOpen, onClose, languages }: Props) {
  const { i18n } = useTranslation()
  const { LOCAL_STORAGE_I18N_KEY } = useCampaignSlug()

  const handleChange = (code: string) => {
    // 同じ言語が選ばれた場合は何もせずに閉じる（不要なリロードを避ける）
    if (i18n.language === code) {
      onClose()
      return
    }

    // API のリクエストヘッダー（locale）は localStorage の値を参照しているため、
    // 先に保存してからページをリロードし、全 API のレスポンスを新しい言語で取得し直す
    let isPersisted = false
    try {
      localStorage.setItem(LOCAL_STORAGE_I18N_KEY, code)
      isPersisted = true
    } catch {
      // localStorage が使用不可（Safari プライベートモード等）
    }

    // 保存できなかった場合はリロードしても言語が元に戻ってしまうため、
    // リロードせずに画面上の表示のみを切り替える
    if (!isPersisted) {
      void i18n.changeLanguage(code).catch(() => {
        // 言語リソースはバンドル済みのため実質発生しないが、未処理の rejection を防ぐ
      })
      onClose()
      return
    }

    applyLocaleAndReload(i18n, code)
  }

  return (
    <Dialog selectorState={isOpen} closeFunc={onClose} title="Language">
      <ul className="flex flex-col gap-3 px-4 py-4">
        {languages.map((lang) => (
          <li key={lang.id}>
            <button
              type="button"
              onClick={() => handleChange(lang.code)}
              className={`w-full rounded-lg border px-4 py-3 text-left text-h1 font-bold transition-colors
                ${i18n.language === lang.code ? 'border-main text-main' : 'border-gray-200 text-gray-700'}`}
            >
              {lang.label}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  )
}
